import { useState } from 'react';
import { CheckOutlined, StopOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  Flex,
  Grid,
  Input,
  List,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { LacunaDoAcervo } from '@titularis/api-client';
import { api } from '../api';
import { comoLer, questoesTravadas } from './acervo/lacuna';
import { formatarData } from '../dominio';

/**
 * O backlog de ingestão do acervo (D-39).
 *
 * **É a outra ponta do Comentário Assistido, e a razão de ele devolver.** Quando
 * o motor não acha no acervo o que a questão exige, ele devolve nomeando a falta;
 * a falta aparece aqui com quantas questões travou, alguém ingere a fonte, e as
 * questões voltam sozinhas à lista de trabalho. Cada volta melhora o catálogo
 * **retroativamente**.
 *
 * **Ordenado por impacto, e não por data.** Lacuna que travou oito questões paga
 * a ingestão mais depressa do que a que travou uma - sem a contagem à vista, a
 * escolha sairia pela ordem alfabética do que aparecesse na tela.
 *
 * **Resolver e descartar fecham a lacuna e devolvem as questões**, e significam
 * coisas opostas: uma diz "o acervo passou a ter", a outra diz "não vai ter".
 * Nos dois casos a espera perdeu o objeto, e manter a questão parada produziria
 * item preso para sempre esperando algo que ninguém vai ingerir.
 */

/** O mesmo corte das demais telas do admin: abaixo de `md`, tabela vira lista. */
const useCompacto = () => !Grid.useBreakpoint().md;

type Acao = { lacuna: LacunaDoAcervo; tipo: 'resolver' | 'descartar' };

export function LacunasDoAcervo() {
  const compacto = useCompacto();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [acao, setAcao] = useState<Acao | null>(null);
  const [nota, setNota] = useState('');

  const lacunas = useQuery({
    queryKey: ['corpus', 'lacunas'],
    queryFn: () => api.listCorpusGaps(),
  });

  const fechar = useMutation({
    mutationFn: ({ lacuna, tipo }: Acao) =>
      tipo === 'resolver'
        ? api.resolveCorpusGap(lacuna.id, nota || undefined)
        : api.discardCorpusGap(lacuna.id, nota || undefined),
    onSuccess: (resultado) => {
      /*
       * O número de questões liberadas é o ponto da mensagem, e não um detalhe:
       * é ele que mostra que fechar a lacuna destravou trabalho. "Lacuna
       * resolvida" sozinho não diria que alguma coisa voltou para a fila.
       */
      message.success(
        resultado.questoesLiberadas > 0
          ? `Lacuna fechada. ${resultado.questoesLiberadas} questão(ões) voltaram para a lista de trabalho.`
          : 'Lacuna fechada. Nenhuma questão estava esperando só por ela.',
      );
      setAcao(null);
      setNota('');
      /*
       * Invalida a fila junto: as questões liberadas mudam de estado lá, e uma
       * tela dizendo "voltaram para a lista" com a lista mostrando o contrário
       * seria pior do que não avisar.
       */
      void queryClient.invalidateQueries({ queryKey: ['corpus'] });
      void queryClient.invalidateQueries({ queryKey: ['comment-jobs'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const lista = lacunas.data ?? [];
  const travadas = questoesTravadas(lista);
  const fontesInteiras = lista.filter((l) => l.address === null);

  const colunas = [
    {
      title: 'O que falta',
      key: 'falta',
      render: (_: unknown, lacuna: LacunaDoAcervo) => (
        <Flex vertical gap={2} style={{ minWidth: 0 }}>
          <Typography.Text code>{comoLer(lacuna)}</Typography.Text>
          {lacuna.note ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {lacuna.note}
            </Typography.Text>
          ) : null}
        </Flex>
      ),
    },
    {
      title: 'Alcance',
      key: 'alcance',
      width: 150,
      render: (_: unknown, lacuna: LacunaDoAcervo) =>
        lacuna.address === null ? (
          <Tooltip title="Falta a fonte inteira: o conserto é importá-la.">
            <Tag color="red">fonte inteira</Tag>
          </Tooltip>
        ) : (
          <Tooltip title="A fonte está no acervo e este dispositivo não entrou: o conserto é reimportá-la.">
            <Tag>dispositivo</Tag>
          </Tooltip>
        ),
    },
    {
      title: 'Questões travadas',
      key: 'impacto',
      width: 160,
      render: (_: unknown, lacuna: LacunaDoAcervo) => (
        <Tooltip title={`Relatada ${lacuna.reportCount} vez(es) por motores de comentário.`}>
          <Typography.Text strong>{lacuna.questoes.length}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: 'Última vez',
      key: 'quando',
      width: 160,
      render: (_: unknown, lacuna: LacunaDoAcervo) => (
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {formatarData(lacuna.lastReportedAt ?? lacuna.createdAt)}
        </Typography.Text>
      ),
    },
    {
      title: '',
      key: 'acoes',
      width: 200,
      render: (_: unknown, lacuna: LacunaDoAcervo) => (
        <Space size="small">
          <Button
            size="small"
            icon={<CheckOutlined />}
            onClick={() => {
              setAcao({ lacuna, tipo: 'resolver' });
              setNota('');
            }}
          >
            Resolver
          </Button>
          <Button
            size="small"
            danger
            icon={<StopOutlined />}
            onClick={() => {
              setAcao({ lacuna, tipo: 'descartar' });
              setNota('');
            }}
          >
            Descartar
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        O que o Comentário Assistido procurou e não achou. Enquanto a falta está aberta, as questões
        que dependiam dela ficam <Typography.Text code>aguardando acervo</Typography.Text> e nenhum
        motor as pega - é o que impede o próximo de gastar a mesma chamada para topar com a mesma
        parede. Ingerida a fonte, elas voltam sozinhas para a lista.
      </Typography.Text>

      {fontesInteiras.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          title={`${fontesInteiras.length} fonte(s) faltam por inteiro no acervo`}
          description={
            `${fontesInteiras.map(comoLer).join(', ')}. Não é dispositivo que não entrou: a fonte ` +
            'não está no acervo. Legislação entra pelo catálogo versionado (aba Legislação); ' +
            'jurisprudência e doutrina, por upload na aba ao lado.'
          }
        />
      ) : null}

      <Card size={compacto ? 'small' : undefined}>
        <Space size="large" wrap style={{ marginBottom: 16 }}>
          <Typography.Text>
            <strong>{lista.length}</strong> lacuna(s) aberta(s)
          </Typography.Text>
          <Typography.Text type="secondary">
            {travadas} questão(ões) esperando acervo
          </Typography.Text>
        </Space>

        {lista.length === 0 && !lacunas.isLoading ? (
          /*
           * O vazio aqui é boa notícia, e a mensagem precisa dizer isso - senão
           * lê-se como tela quebrada. E precisa dizer a outra metade: pode ser
           * que ninguém tenha devolvido nada ainda.
           */
          <Empty
            description={
              <Typography.Text type="secondary">
                Nenhuma lacuna aberta. Ou o acervo cobriu tudo o que foi pedido, ou ainda não houve
                devolução por falta de material.
              </Typography.Text>
            }
          />
        ) : compacto ? (
          <List<LacunaDoAcervo>
            loading={lacunas.isLoading}
            dataSource={lista}
            renderItem={(lacuna) => (
              <List.Item style={{ paddingInline: 0, alignItems: 'flex-start' }}>
                <Flex vertical gap={6} style={{ width: '100%', minWidth: 0 }}>
                  <Flex align="center" gap={8} wrap>
                    <Typography.Text code>{comoLer(lacuna)}</Typography.Text>
                    {lacuna.address === null ? (
                      <Tag color="red" style={{ marginInlineEnd: 0 }}>
                        fonte inteira
                      </Tag>
                    ) : null}
                  </Flex>

                  {lacuna.note ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {lacuna.note}
                    </Typography.Text>
                  ) : null}

                  <Typography.Text style={{ fontSize: 13 }}>
                    <strong>{lacuna.questoes.length}</strong>{' '}
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      questão(ões) travada(s) · {formatarData(lacuna.lastReportedAt ?? lacuna.createdAt)}
                    </Typography.Text>
                  </Typography.Text>

                  {/* As duas ações viram a última linha do cartão: numa tabela de
                      cinco colunas seriam a primeira coisa a sair do campo de visão. */}
                  <Space size="small">
                    <Button
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={() => {
                        setAcao({ lacuna, tipo: 'resolver' });
                        setNota('');
                      }}
                    >
                      Resolver
                    </Button>
                    <Button
                      size="small"
                      danger
                      icon={<StopOutlined />}
                      onClick={() => {
                        setAcao({ lacuna, tipo: 'descartar' });
                        setNota('');
                      }}
                    >
                      Descartar
                    </Button>
                  </Space>
                </Flex>
              </List.Item>
            )}
          />
        ) : (
          <Table<LacunaDoAcervo>
            rowKey="id"
            loading={lacunas.isLoading}
            dataSource={lista}
            columns={colunas}
            pagination={false}
            size="middle"
          />
        )}
      </Card>

      <Modal
        open={acao !== null}
        title={acao?.tipo === 'resolver' ? 'Resolver a lacuna' : 'Descartar a lacuna'}
        okText={acao?.tipo === 'resolver' ? 'Resolver' : 'Descartar'}
        okButtonProps={{ danger: acao?.tipo === 'descartar' }}
        cancelText="Cancelar"
        confirmLoading={fechar.isPending}
        onOk={() => acao && fechar.mutate(acao)}
        onCancel={() => {
          setAcao(null);
          setNota('');
        }}
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text>
            {acao ? <Typography.Text code>{comoLer(acao.lacuna)}</Typography.Text> : null}
          </Typography.Text>

          <Typography.Text type="secondary">
            {acao?.tipo === 'resolver'
              ? 'Use depois de ingerir a fonte. As questões que esperavam por esta falta voltam para a lista de trabalho.'
              : 'Use quando a falta não vai ser suprida. As questões voltam para a lista mesmo assim, porque a espera perdeu o objeto.'}
          </Typography.Text>

          {acao && acao.lacuna.questoes.length > 0 ? (
            <Typography.Text type="secondary">
              {acao.lacuna.questoes.length} questão(ões) serão devolvidas à lista.
            </Typography.Text>
          ) : null}

          {/* A nota vai para a trilha (G-AUDIT): fechar item de backlog muda o
              que a pipeline vai tentar comentar, e é decisão que se precisa poder
              explicar depois. */}
          <Input.TextArea
            rows={3}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            maxLength={500}
            placeholder={
              acao?.tipo === 'resolver'
                ? 'Por que resolveu: "importada em lote", por exemplo.'
                : 'Por que descartou: "não é fonte oficial", por exemplo.'
            }
          />
        </Space>
      </Modal>
    </Space>
  );
}
